import { REVENUE_BUDGET_SWITCH_DATE } from '@cortex/domain-ac-mens-hub-api';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTargets, useSyncTargets } from '../hooks/useBudgetActual.js';
import { OfferBreakdownSection } from '../components/budget-actual/OfferBreakdownSection.js';
import { OfferExcludedSection } from '../components/budget-actual/OfferExcludedSection.js';
import { type OfferStackConfig, TargetChart } from '../components/budget-actual/TargetChart.js';
import {
  BUDGET_TABS,
  GRANULARITY_LABELS,
  type Granularity,
  type SyncResponse,
} from '../types/budget-actual.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';

/**
 * 実績の棒を「オファー抜き」と「オファーによる上乗せ」に積むカード（合計CPA）の設定。
 * オファーの内訳も、このカード（下段の指標キー）を含むグループの直後に置く
 * （グループの名前やタブの番号に頼らず、指標キーで決める）。
 * @graph-connects none
 */
const OFFER_STACK: OfferStackConfig = {
  lowerKey: 'cpaTotalVal',
  withOfferKey: 'cpaTotalWithOffer',
};

/**
 * 同期完了の文言に並べる要素ごとの文言のキー。文言は要素ごとの部品をつないで作り、組み合わせごとの文言は持たない。
 * @graph-connects none
 */
const SYNC_PART_KEYS = {
  monthly: 'budgetActual.syncPart.monthly',
  daily: 'budgetActual.syncPart.daily',
  acquisition: 'budgetActual.syncPart.acquisition',
  offer: 'budgetActual.syncPart.offer',
} as const;

/**
 * 同期完了の文言に件数を出す要素と件数（並びは 月次・日次・獲得実績・オファー内訳）。獲得実績は取り込めたとき
 * （success）、オファー内訳は行を取り込んだとき（success・stale）だけ出す。失敗・まだ始まっていないときの 0 は
 * 「0 行取り込んだ」ではないため（失敗は別の帯で伝える）。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 同期完了の文言に並べる要素（月次・日次・獲得実績・オファー内訳）を、取り込めた要素だけに絞る
 * @graph-connects none
 */
function syncSummaryParts(
  data: SyncResponse,
): { key: keyof typeof SYNC_PART_KEYS; rows: number }[] {
  const parts: { key: keyof typeof SYNC_PART_KEYS; rows: number }[] = [
    { key: 'monthly', rows: data.monthlyCount },
    { key: 'daily', rows: data.dailyCount },
  ];
  if (data.acquisitionStatus === 'success') {
    parts.push({ key: 'acquisition', rows: data.acquisitionCount });
  }
  if (data.offerStatus === 'success' || data.offerStatus === 'stale') {
    parts.push({ key: 'offer', rows: data.offerCount });
  }
  return parts;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 予実管理ページ。事業計画の目標を4タブ（売上・会員/広告・CPA/獲得チャネル/CS・品質）×粒度切替で可視化し、スプシ同期ボタンを提供する。売上実績の出どころが切り替わる日（正式版 v0.1 の売上高へ）を注記し、まだ計算されていない日以降の売上実績の 0 が未計算であること、台帳を読み出せなかったときは売上実績を出していないことを示す。合計CPA（cpaTotalVal）のカードだけ実績を「オファー抜き」と「オファーによる上乗せ」に積む設定を渡し、そのカードを含むグループの直後にオファーの内訳（ページの期間で集計し、粒度切替には連動しない）を出し、その直後に合計CPA（オファーあり）に含めない割引（株主優待・福利厚生）の表を出す。同期の結果は獲得実績とオファー内訳の取り込みの成否と、オファー内訳の毎朝の更新が止まっていることも出す（まだ始まっていないときは出さない）。同期完了の文言は要素ごとの部品をつなぎ、獲得実績は取り込めたとき（success）、オファー内訳は取り込んだとき（success・stale）だけ行数を出す。
 * @graph-connects syncSummaryParts [calls] 同期完了の文言に並べる要素
 * @graph-connects useTargets [calls] 予実目標取得フック
 * @graph-connects useSyncTargets [calls] 同期ミューテーション
 * @graph-connects TargetChart [calls] 汎用予実チャート
 * @graph-connects OfferBreakdownSection [calls] オファーの内訳
 * @graph-connects OfferExcludedSection [calls] 合計CPAに含めない割引（オファーの内訳の直後）
 */
export function BudgetActualPage() {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [activeTab, setActiveTab] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, error } = useTargets(from || undefined, to || undefined, granularity);
  const syncMutation = useSyncTargets();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mx-auto max-w-lg p-6 text-center">
        <p className="text-sm text-[var(--color-text-danger)]">
          {t('common.fetchError')}: {error.message}
        </p>
      </Card>
    );
  }

  const rows = data?.rows ?? [];
  const columns = data?.columns ?? [];
  const actuals = data?.actuals ?? [];
  const availableActualMetrics = new Set(data?.availableActualMetrics ?? []);

  const tabColumns = columns.filter(
    (col) =>
      col.tab === activeTab && (granularity === 'monthly' || !col.key.startsWith('channelRatio')),
  );
  const groups = [...new Set(tabColumns.map((col) => col.group))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('budgetActual.title')}
        </h1>
        <div className="flex items-center gap-3">
          {data?.lastSyncedAt && (
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('budgetActual.lastSync')}: {new Date(data.lastSyncedAt).toLocaleString('ja-JP')}
            </span>
          )}
          <button
            type="button"
            className="rounded-md bg-[var(--color-bg-accent)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-on-accent)] transition-opacity hover:opacity-80 disabled:opacity-50"
            disabled={syncMutation.isPending}
            onClick={() => {
              syncMutation.mutate();
            }}
          >
            {syncMutation.isPending ? t('budgetActual.syncing') : t('budgetActual.sync')}
          </button>
        </div>
      </div>

      <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      <p className="text-xs text-[var(--color-text-secondary)]">
        {t('budgetActual.revenueSwitchNote', { date: REVENUE_BUDGET_SWITCH_DATE })}
      </p>
      {data?.revenueFailure != null && (
        <p role="alert" className="text-xs text-[var(--color-error)]">
          {t('budgetActual.revenueFailed', {
            reason: data.revenueFailure.reason,
            date: data.revenueFailure.from,
          })}
        </p>
      )}
      {data?.revenueUncomputedFrom != null && (
        <p role="status" className="text-xs text-[var(--color-warning)]">
          {t('budgetActual.revenueUncomputed', { date: data.revenueUncomputedFrom })}
        </p>
      )}

      <div className="flex items-center gap-4">
        <div className="flex gap-1 rounded-lg bg-[var(--color-bg-secondary)] p-1">
          {BUDGET_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
              onClick={() => {
                setActiveTab(tab.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1 rounded-lg bg-[var(--color-bg-secondary)] p-1">
          {(Object.entries(GRANULARITY_LABELS) as [Granularity, string][]).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                granularity === key
                  ? 'bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
              onClick={() => {
                setGranularity(key);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {groups.map((group) => {
        const groupCols = tabColumns.filter((col) => col.group === group);
        return (
          <Fragment key={group}>
            <section>
              <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">
                {group}
              </h2>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {groupCols.map((col) => (
                  <TargetChart
                    key={col.key}
                    title={col.label}
                    metricKey={col.key}
                    rows={rows}
                    actuals={actuals}
                    hasActual={availableActualMetrics.has(col.key)}
                    offerStack={col.key === OFFER_STACK.lowerKey ? OFFER_STACK : undefined}
                  />
                ))}
              </div>
            </section>
            {groupCols.some((col) => col.key === OFFER_STACK.lowerKey) && (
              <>
                <OfferBreakdownSection from={from || undefined} to={to || undefined} />
                <OfferExcludedSection from={from || undefined} to={to || undefined} />
              </>
            )}
          </Fragment>
        );
      })}

      {syncMutation.isSuccess && (
        <div className="space-y-2">
          <div className="rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
            {t('budgetActual.syncSuccess', {
              parts: syncSummaryParts(syncMutation.data)
                .map(({ key, rows }) => t(SYNC_PART_KEYS[key], { rows }))
                .join(t('budgetActual.syncPartSeparator')),
            })}
          </div>
          {syncMutation.data.acquisitionStatus === 'failed' && (
            <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
              {t('budgetActual.acquisitionSyncFailed')}
            </div>
          )}
          {syncMutation.data.offerStatus === 'failed' && (
            <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
              {t('budgetActual.offerSyncFailed')}
            </div>
          )}
          {syncMutation.data.offerStatus === 'stale' && (
            <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
              {t('budgetActual.offerSyncStale', { at: syncMutation.data.offerLastComputedAt })}
            </div>
          )}
        </div>
      )}

      {syncMutation.isError && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
          {t('budgetActual.syncError')}: {syncMutation.error.message}
        </div>
      )}
    </div>
  );
}
